/**
 * Design Scaling Utilities
 *
 * Functions for scaling designs within print areas.
 * Used by ImagePropertiesBar for Fit/Fill functionality.
 *
 * Note: For Fabric.js-independent contain/cover scaling, use:
 * import { containScale, coverScale } from '@designdeck/shared';
 */

import { Point, type FabricObject } from 'fabric';
import type { PrintArea } from '@/types/canvas';

// Re-export shared math utilities for convenience
export { containScale, coverScale } from '@designdeck/shared';

/**
 * Moves the object's centre onto the print area's centre. Goes through the
 * object's own origin rather than assuming a top-left one: motifs and texts
 * are added with a centre origin, where writing the top-left corner into
 * left/top shifted them by half their size.
 */
function centerInArea(object: FabricObject, printArea: PrintArea): void {
  object.setPositionByOrigin(
    new Point(
      printArea.x + printArea.width / 2,
      printArea.y + printArea.height / 2
    ),
    'center',
    'center'
  );
}

/**
 * Fit design to area - Scales design to 80% width of print area
 *
 * The design remains completely within the print area.
 * If the resulting height would exceed the area, scales by height instead.
 *
 * @param object - The Fabric.js object to scale
 * @param printArea - The print area boundaries
 * @param targetWidthPercent - Target width as percentage (default: 0.8 = 80%)
 */
export function fitDesignToArea(
  object: FabricObject,
  printArea: PrintArea,
  targetWidthPercent = 0.8
): void {
  const objectWidth = object.width || 1;
  const objectHeight = object.height || 1;

  // Target width: 80% of print area
  const targetWidth = printArea.width * targetWidthPercent;
  const scaleForWidth = targetWidth / objectWidth;

  // Check if height still fits
  const resultingHeight = objectHeight * scaleForWidth;
  const maxHeight = printArea.height * 0.95; // 5% buffer

  // If height exceeds, scale by height instead
  const finalScale =
    resultingHeight > maxHeight ? maxHeight / objectHeight : scaleForWidth;

  object.scale(finalScale);
  centerInArea(object, printArea);
  object.setCoords();
}

/**
 * Fill design to area - Scales design to maximum size within print area
 *
 * Fills the print area completely (until one edge is reached).
 * Uses contain-like behavior (no overflow).
 *
 * @param object - The Fabric.js object to scale
 * @param printArea - The print area boundaries
 */
export function fillDesignToArea(
  object: FabricObject,
  printArea: PrintArea
): void {
  const objectWidth = object.width || 1;
  const objectHeight = object.height || 1;

  const scaleX = printArea.width / objectWidth;
  const scaleY = printArea.height / objectHeight;

  // Use smaller scale (so nothing overflows)
  const finalScale = Math.min(scaleX, scaleY);

  object.scale(finalScale);
  centerInArea(object, printArea);
  object.setCoords();
}

/**
 * Scale design by percentage
 *
 * Scales the design proportionally based on a percentage value.
 * 100% = current size, 50% = half size, 200% = double size
 *
 * @param object - The Fabric.js object to scale
 * @param baseScale - The base scale (scale at 100%)
 * @param percent - The target percentage (50-200)
 */
export function scaleDesignByPercent(
  object: FabricObject,
  baseScale: number,
  percent: number
): void {
  const clampedPercent = Math.max(50, Math.min(200, percent));
  const newScale = baseScale * (clampedPercent / 100);
  object.scale(newScale);
  object.setCoords();
}

/**
 * Set design width in physical units (cm)
 *
 * @param object - The Fabric.js object to scale
 * @param widthCm - Target width in centimeters
 * @param pxPerMm - Pixels per millimeter ratio
 * @param proportional - Whether to scale proportionally
 */
export function setDesignWidthCm(
  object: FabricObject,
  widthCm: number,
  pxPerMm: number,
  proportional = true
): void {
  const objectWidth = object.width || 1;
  const widthMm = widthCm * 10;
  const widthPx = widthMm * pxPerMm;
  const newScaleX = widthPx / objectWidth;

  if (proportional) {
    object.scale(newScaleX);
  } else {
    object.set('scaleX', newScaleX);
  }
  object.setCoords();
}

/**
 * Set design height in physical units (cm)
 *
 * @param object - The Fabric.js object to scale
 * @param heightCm - Target height in centimeters
 * @param pxPerMm - Pixels per millimeter ratio
 * @param proportional - Whether to scale proportionally
 */
export function setDesignHeightCm(
  object: FabricObject,
  heightCm: number,
  pxPerMm: number,
  proportional = true
): void {
  const objectHeight = object.height || 1;
  const heightMm = heightCm * 10;
  const heightPx = heightMm * pxPerMm;
  const newScaleY = heightPx / objectHeight;

  if (proportional) {
    object.scale(newScaleY);
  } else {
    object.set('scaleY', newScaleY);
  }
  object.setCoords();
}
